import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCurrentInstructorDetail,
  getInstructorDetail,
  resetAllActions,
} from "../../State/Instructor/Action";
import { motion } from "framer-motion";
import {
  FaEdit,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaUserGraduate,
  FaGlobe,
  FaAward,
  FaChalkboardTeacher,
  FaLanguage,
  FaGraduationCap,
} from "react-icons/fa";

const InstructorDetail = () => {
  const { instructorId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const instructor = useSelector(
    (state) => state.instructor?.currentInstructor?.data
  );

  useEffect(() => {
    dispatch(getCurrentInstructorDetail());
    dispatch(resetAllActions());
  }, [dispatch]);

  if (!instructor) {
    return (
      <div className="p-10 text-center">
        <p className="text-lg font-medium animate-pulse">
          Loading Instructor Details...
        </p>
      </div>
    );
  }

  const DetailRow = ({ icon, label, value }) => {
    if (!value) return null;
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-2 py-2 items-center"
      >
        <span className="flex items-center gap-2 font-semibold text-gray-700">
          {icon}
          {label}:
        </span>
        <span className="text-gray-800">{value}</span>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-100 to-indigo-100 py-10 px-4">
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-5xl mx-auto bg-white/90 shadow-2xl rounded-3xl p-8 backdrop-blur-lg"
      >
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate("/profile-edit")}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
          >
            <FaEdit />
            Edit Profile
          </button>
        </div>

        <div className="flex flex-col items-center mb-8">
          <motion.img
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            src={instructor.profilePictureUrl}
            alt="Instructor"
            className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-indigo-300 shadow-md"
          />
          <h1 className="text-4xl font-bold text-gray-800">
            {instructor.fullName}
          </h1>
          <p className="text-indigo-600 font-semibold mt-1 text-lg">
            {instructor.headline}
          </p>
        </div>

        <div className="border-t border-gray-300 pt-6">
          <DetailRow
            icon={<FaUser />}
            label="Username"
            value={instructor.username}
          />
          <DetailRow
            icon={<FaEnvelope />}
            label="Email"
            value={instructor.email}
          />
          <DetailRow
            icon={<FaPhone />}
            label="Phone"
            value={instructor.phone}
          />
          <DetailRow
            icon={<FaChalkboardTeacher />}
            label="Title"
            value={instructor.title}
          />
          <DetailRow
            icon={<FaUserGraduate />}
            label="Bio"
            value={instructor.bio}
          />
          <DetailRow
            icon={<FaGlobe />}
            label="LinkedIn"
            value={instructor.linkedIn}
          />
          <DetailRow
            icon={<FaGlobe />}
            label="Website"
            value={instructor.website}
          />
          <DetailRow
            icon={<FaChalkboardTeacher />}
            label="Experience"
            value={`${instructor.experience} years`}
          />
          <DetailRow
            icon={<FaGraduationCap />}
            label="Education"
            value={instructor.education}
          />
          <DetailRow
            icon={<FaAward />}
            label="Certifications"
            value={instructor.certifications}
          />
        </div>

        {instructor.expertise?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <h2 className="text-xl font-semibold text-indigo-700 mb-3 flex items-center gap-2">
              🌟 Areas of Expertise
            </h2>
            <div className="flex flex-wrap gap-2">
              {instructor.expertise.map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm shadow-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {instructor.languages?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <h2 className="text-xl font-semibold text-green-700 mb-3 flex items-center gap-2">
              🗣️ Languages Spoken
            </h2>
            <div className="flex flex-wrap gap-2">
              {instructor.languages.map((lang) => (
                <span
                  key={lang}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm shadow-sm"
                >
                  {lang}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default InstructorDetail;
